import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, ArrowUpRight, Sparkles } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import PageHero from '../components/PageHero';
import { SPONSORS } from '../data/mock';
import { api } from '../lib/api';
import { EditRegion } from '../components/AdminEditBar';
import { InlineText } from '../components/InlineEditable';

export default function Sponsors() {
  useSEO('sponsors');
  const [items, setItems] = useState(SPONSORS);
  const [fromDb, setFromDb] = useState(false);

  useEffect(() => {
    api.publicContent('sponsors').then(list => {
      if (Array.isArray(list) && list.length > 0) {
        setItems(list.map(it => ({ id: it.id, ...(it.data || {}) })));
        setFromDb(true);
      }
    }).catch(() => {});
  }, []);

  // Marquee logos = all sponsors with logo
  const marqueeLogos = items.filter(s => s.logo).slice(0, 20);
  const marqueeItems = marqueeLogos.length > 0 ? [...marqueeLogos, ...marqueeLogos] : [];

  return (
    <>
      <PageHero
        slug="sponsors"
        kicker="Destekçilerimiz"
        title={<>Birlikte <span className="italic text-coral-200">daha güçlü</span>.</>}
        text="Vakfımızın büyük projelerini kurumsal destekçilerimiz, stratejik ortaklarımız ve akademik işbirliklerimizle hayata geçiriyoruz."
      />

      {/* Auto-scrolling logo marquee — trust strip */}
      {marqueeItems.length > 0 && (
        <section className="bg-cream border-y border-ink-100 overflow-hidden py-8">
          <div className="text-[10px] uppercase tracking-[0.3em] text-ink-400 text-center font-bold mb-5 inline-flex items-center gap-2 w-full justify-center">
            <Sparkles className="w-3 h-3 text-gold" />
            <InlineText settingKey="sponsors_marquee_kicker" fallback="30+ yıldır güvenilir kurumların desteğiyle" as="span" />
            <Sparkles className="w-3 h-3 text-gold" />
          </div>
          <div className="relative">
            <div className="flex gap-12 md:gap-16 animate-marquee whitespace-nowrap">
              {marqueeItems.map((s, i) => (
                <div key={`${s.id || 'x'}-${i}`} className="flex items-center shrink-0 h-16 opacity-60 hover:opacity-100 transition-opacity">
                  <img src={s.logo} alt={s.name} className="max-h-14 w-auto object-contain" />
                </div>
              ))}
            </div>
            {/* Gradient masks */}
            <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-cream to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-cream to-transparent" />
          </div>
        </section>
      )}

      <section className="bg-cream">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6" data-testid="sponsors-grid">
            {items.map((s, i) => (
              <EditRegion key={s.id || i} label="Destekçiyi Düzenle" to="/admin/sponsors">
                <SponsorCard s={s} />
              </EditRegion>
            ))}
          </div>

          {items.length === 0 && (
            <div className="text-center py-16 text-ink-400 bg-white border border-ink-100 rounded-3xl">Henüz destekçi eklenmedi.</div>
          )}

          {/* Admin add-sponsor CTA — visible only in edit mode */}
          <div className="mt-8 yekuv-admin-only text-center" style={{ display: 'none' }}>
            <Link to="/admin/sponsors" data-testid="add-sponsor" className="inline-flex items-center gap-2 bg-ink-600 text-cream rounded-full px-6 h-12 text-sm font-semibold hover:bg-ink-500">
              <Plus className="w-4 h-4" /> Yeni Destekçi Ekle {fromDb ? '' : '(mock veri kullanılıyor — ilk kayıt yapıldığında DB devreye girer)'}
            </Link>
          </div>

          <div className="mt-20 relative overflow-hidden rounded-[32px] bg-ink-600 text-cream p-10 md:p-14 grid md:grid-cols-2 gap-8 items-center">
            <div className="absolute -top-24 -right-24 w-[400px] h-[400px] rounded-full bg-gold/15 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full bg-coral-300/10 blur-3xl pointer-events-none" />
            <div className="relative">
              <InlineText settingKey="sponsors_cta_kicker" fallback="Kurumsal Destek" as="div"
                className="text-[11px] tracking-[0.3em] uppercase text-gold font-semibold" />
              <InlineText settingKey="sponsors_cta_title" fallback="Kurumunuzu YEKÜV ailesine davet ediyoruz." as="h3"
                multiline className="font-display text-3xl md:text-4xl mt-3 leading-tight" />
              <InlineText settingKey="sponsors_cta_text" fallback="Kurumsal destekçi olmak, vergi avantajı sağlamanın ötesinde; toplumsal etki yaratmanın en etkili yoludur." as="p"
                multiline className="mt-4 text-cream/75" />
            </div>
            <div className="relative flex md:justify-end">
              <a href="/gonullu#kurumsal" className="inline-flex items-center gap-2 bg-gold text-ink-700 rounded-full px-7 h-14 font-bold hover:bg-gold-400 transition-colors shadow-lg">Kurumsal Destek Formu <ArrowUpRight className="w-4 h-4" /></a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

/** Sponsor card — no category chip, clean logo focus. */
function SponsorCard({ s }) {
  const Wrap = s.website ? 'a' : 'div';
  const wrapProps = s.website ? { href: s.website, target: '_blank', rel: 'noopener noreferrer' } : {};
  return (
    <Wrap {...wrapProps} className="group relative block bg-white rounded-3xl border border-ink-100 overflow-hidden transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-ink-900/10 hover:border-gold/50">
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-br from-cream-200/30 to-transparent pointer-events-none" />
      <div className="relative p-6 min-h-[220px] flex flex-col">
        <div className="flex-1 flex items-center justify-center py-6">
          {s.logo ? (
            <img src={s.logo} alt={s.name} className="max-h-20 md:max-h-24 w-auto max-w-[75%] object-contain transition-transform duration-500 group-hover:scale-105" />
          ) : (
            <div className="font-display text-2xl md:text-3xl text-ink-600 text-center leading-tight">{s.name}</div>
          )}
        </div>
        <div className="flex items-end justify-between gap-2">
          <div className="font-display text-lg text-ink-600 leading-tight line-clamp-2 flex-1">{s.name}</div>
          {s.website && <ArrowUpRight className="w-4 h-4 text-ink-300 group-hover:text-ink-600 shrink-0" />}
        </div>
      </div>
    </Wrap>
  );
}
